import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import type { RecordRef } from "../services/ActionReports/ActionReportAPI.types";

/**
 * Redux state for the Japan Group Leader filter applied across all Action
 * Reports. Only one leader is selectable at a time.
 *
 * `selectedLeader` is null when no filter is applied (default "All" view).
 * Visibility of the filter UI is decided upstream by whether the backend
 * response includes `groupLeaders` — the frontend never reads market config
 * directly.
 */
export interface GroupLeaderFilterState {
  selectedLeader: RecordRef | null;
}

export const initialState: GroupLeaderFilterState = {
  selectedLeader: null,
};

const { actions, reducer } = createSlice({
  name: "groupLeaderFilter",
  initialState,
  reducers: {
    /** Select a leader, or clear the selection with `null`. */
    setSelectedLeader: (state, action: PayloadAction<RecordRef | null>) => {
      state.selectedLeader = action.payload;
    },
    /** Clear the selection on route change between Action Reports. */
    resetGroupLeaderFilter: () => initialState,
  },
});

export const { setSelectedLeader, resetGroupLeaderFilter } = actions;

/** Redux selector for the currently selected group leader (null when unset). */
export const selectGroupLeader = (state: RootState): RecordRef | null => state.groupLeaderFilter.selectedLeader;

/**
 * Encode a RecordRef as a URL query-param value. Zero-value refs encode to
 * the empty string so callers can drop the key from the URL.
 */
export const encodeGroupLeaderParam = (leader: RecordRef | null): string => {
  if (!leader || (!leader.aff && !leader.abo)) return "";
  return `${leader.aff}-${leader.abo}`;
};

/**
 * Decode a URL query-param value into a RecordRef. Returns null when the
 * value is malformed. Both segments must be numeric — the value flows into
 * Redux and downstream into export payloads, so we refuse anything that
 * isn't shaped like a real aff/abo pair.
 */
const NUMERIC_SEGMENT = /^\d{1,10}$/;
export const decodeGroupLeaderParam = (raw: string | null): RecordRef | null => {
  if (!raw) return null;
  const parts = raw.split("-");
  if (parts.length !== 2) return null;
  const [aff, abo] = parts;
  if (!NUMERIC_SEGMENT.test(aff) || !NUMERIC_SEGMENT.test(abo)) return null;
  return { aff, abo };
};

export default reducer;
